import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Eye, Pencil } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { PageToolbar } from '@/components/layout/PageToolbar'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PageError } from '@/components/ui/PageAsyncState'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { useCan } from '@/features/auth/AuthContext'
import { computeCustomerListKpis } from './customerListMetrics'
import { listCustomersApi, type Customer } from '@/services/customers'

const fieldClass =
  'h-8 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 text-sm outline-none focus:border-[var(--color-accent)]'

export function CustomerListPage() {
  const navigate = useNavigate()
  const canWrite = useCan('sales.write')
  const [rows, setRows] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      const data = await listCustomersApi({ q, status: statusFilter })
      setRows(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được danh sách khách hàng')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [q, statusFilter])

  const kpis = useMemo(() => computeCustomerListKpis(rows), [rows])

  const columns: ProtoColumn<Customer>[] = useMemo(
    () => [
      {
        id: 'code',
        header: 'Mã KH',
        sortable: true,
        hideable: false,
        width: 130,
        cell: (r) => (
          <span className="font-mono text-xs font-semibold text-slate-800">
            {r.code}
          </span>
        ),
        sortValue: (r) => r.code,
      },
      {
        id: 'name',
        header: 'Tên khách hàng',
        sortable: true,
        width: 260,
        cell: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
        sortValue: (r) => r.name,
      },
      {
        id: 'buyer_count',
        header: 'Số người mua',
        sortable: true,
        width: 120,
        cell: (r) => (
          <span className="text-right tabular-nums">{r.buyer_count ?? 0}</span>
        ),
        sortValue: (r) => r.buyer_count ?? 0,
      },
      {
        id: 'open_order_count',
        header: 'Đơn hàng mở',
        sortable: true,
        width: 130,
        cell: (r) => (
          <span className="text-right tabular-nums font-medium text-slate-700">
            {r.open_order_count ?? 0}
          </span>
        ),
        sortValue: (r) => r.open_order_count ?? 0,
      },
      {
        id: 'status',
        header: 'Trạng thái',
        sortable: true,
        width: 150,
        cell: (r) => (
          <StatusBadge
            tone={r.status === 'active' ? 'success' : 'neutral'}
            label={r.status === 'active' ? 'Đang hoạt động' : 'Ngừng hoạt động'}
          />
        ),
        sortValue: (r) => r.status,
      },
      {
        id: 'actions',
        header: 'Thao tác',
        sortable: false,
        width: 140,
        cell: (r) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/sales/customers/${r.id}`)
              }}
            >
              <Eye className="h-3.5 w-3.5" />
              Xem
            </button>
            {canWrite && (
              <button
                type="button"
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-amber-600 hover:text-amber-800"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/sales/customers/${r.id}?edit=true`)
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                Sửa
              </button>
            )}
          </div>
        ),
      },
    ],
    [canWrite, navigate],
  )

  return (
    <div className="space-y-4">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Sale', to: '/sales' },
          { label: 'Khách hàng' },
        ]}
      />
      <PageHeader
        title="Khách hàng"
        subtitle="Danh sách khách hàng doanh nghiệp & người mua"
        actions={
          canWrite ? (
            <Button onClick={() => navigate('/sales/customers/new')}>
              <Plus className="h-4 w-4" />
              Tạo khách hàng
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard label="Tổng số KH" value={kpis.totalCustomers} />
        <KpiCard label="Đang hoạt động" value={kpis.activeCustomers} />
        <KpiCard label="Ngừng hoạt động" value={kpis.inactiveCustomers} />
        <KpiCard label="Tổng số người mua" value={kpis.totalBuyers} />
        <KpiCard label="Tổng đơn hàng mở" value={kpis.openOrdersCount} />
      </div>

      <PageToolbar
        left={
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              placeholder="Tìm theo mã hoặc tên KH…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className={fieldClass}
              style={{ width: '100%', maxWidth: '220px' }}
            />
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')
              }
              className={fieldClass}
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="active">Đang hoạt động</option>
              <option value="inactive">Ngừng hoạt động</option>
            </select>
          </div>
        }
      />

      {error && <PageError message={error} onRetry={() => void reload()} />}

      <ProtoExcelTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        searchPlaceholder="Tìm nhanh trong bảng…"
        exportName="danh-sach-khach-hang"
        pageSize={50}
        loading={loading}
        emptyMessage="Chưa có khách hàng nào phù hợp."
        showRowNumbers
        stickyLeftColumnIds={['code']}
        onRowClick={(r) => navigate(`/sales/customers/${r.id}`)}
      />
    </div>
  )
}
